import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, OrthographicCamera, Grid, Detailed, Html, Line } from '@react-three/drei';
import * as THREE from 'three';
import SceneEnvironment from './SceneEnvironment';
import ConfigService, { DEFAULT_SCENE_CONSTANTS } from '../../services/ConfigService';
import RoomService from '../../services/RoomService';
import { getCategoryColor, AXIS_COLORS } from '../../utils/sceneUtils';
import {
    buildOpeningSpacingLabels,
    classifyWallEdges,
    convertMetersValue,
    formatArea,
    formatDimensionTriplet,
    getUnitSystemOptions,
    getUnitSystemSuffix,
    inferRectanglePolygon,
    normalizeUnitSystem,
    UNIT_SYSTEMS,
} from '../../utils/measurementUtils';

// Import CAPTURED sample data
import bedroomCaptured from '../../data/BedroomCapturedData.json';
import kitchenCaptured from '../../data/KitchenCaptureData.json';
import multiRoomCaptured from '../../data/MultiRoomOfficeLivingRawCapturedData.json';

// RoomPlan emits walls/doors/windows as 2D quads with dimensions[2] === 0.
// Rendering a unit box scaled by a zero-Z dimension produces a degenerate
// mesh that z-fights or vanishes at oblique angles. Give planar surfaces
// real thickness so they render as solid slabs.
// Stable empty-set default for the `hiddenIds` prop so the default doesn't
// mint a new reference each render (which would re-run the allRenderObjects memo).
const EMPTY_HIDDEN_IDS = new Set();

// Exported so tests share the source of truth with resolveWallExtrusionThickness.
export const WALL_THICKNESS_M = 0.08;       // ~3 inches; matches typical interior wall framing
const OPENING_THICKNESS_M = 0.06;    // door/window/opening depth; thick enough to stay visible edge-on, < WALL_THICKNESS_M so it nests inside the wall
const FLOOR_THICKNESS_M = 0.02;      // floor polygon depth

// Resolve rendered thickness for a quad whose source may be 2D
// (dimensions[2] === 0). RoomPlan ships walls/doors/windows/openings as
// zero-depth quads; a 2cm slab is invisible edge-on, so inject thickness.
// Dimensions arrive from external scan ingest, so malformed shapes are
// defended here: non-arrays fall back to a unit cube, missing/null
// elements default (width/height 1, depth 0 — a 2D quad), and the
// return is always a 3-element array so boxGeometry never sees a hole.
// Pure + exported so the decision is unit-testable (mesh props aren't
// assertable through the mocked Canvas).
export const resolveOpeningThickness = (renderType, dimensions) => {
    const raw = Array.isArray(dimensions) ? dimensions : [1, 1, 1];
    const width = raw[0] ?? 1;
    const height = raw[1] ?? 1;
    const depth = raw[2] ?? 0;
    if (renderType === 'wall' && depth === 0) {
        return [width, height, WALL_THICKNESS_M];
    }
    if ((renderType === 'door' || renderType === 'window' || renderType === 'opening') && depth === 0) {
        return [width, height, OPENING_THICKNESS_M];
    }
    return [width, height, depth];
};

/**
 * Resolve the EXTRUSION depth for a wall's polygon render path.
 * dimensions[2] (a persisted per-wall depth, set via the editor's Depth
 * field) wins when finite and positive; otherwise fall back to
 * WALL_THICKNESS_M ("assumed standard") — the overwhelming common case,
 * since parametric walls always carry dimensions[2] === 0 and RoomPlan
 * reports walls as 2D quads.
 *
 * Pure + exported for unit testing — the ExtrudeGeometry call isn't
 * inspectable through the mocked Canvas.
 */
export const resolveWallExtrusionThickness = (dimensions) => {
    const depth = Array.isArray(dimensions) ? Number(dimensions[2]) : NaN;
    return Number.isFinite(depth) && depth > 0 ? depth : WALL_THICKNESS_M;
};

// Per-type visual style (opacity + color) for the single-pass render loop.
// Centralizing makes "windows too faint" a unit-testable property instead
// of a buried literal. Returns null for types with no override (objects
// color themselves via getCategoryColor in the mesh).
//
// Solid surfaces (wall/door/floor) are opaque — real walls aren't
// see-through. The dollhouse wall-culler (WallCuller) fades whichever wall
// faces the camera to 30% so the near wall doesn't occlude the interior
// while staying faintly visible. Windows stay glassy: a window is naturally
// transparent, and 0.5 keeps it visible at oblique angles (was 0.3 — too
// faint). Walls cut real holes where their doors/windows/openings sit
// (computeWallHoles), so the panels are visible from both sides of the
// wall and from any angle.
export const getRenderStyle = (renderType) => {
    switch (renderType) {
        // Slightly off-white (light cool gray). Pure white vanished against
        // the near-white studio background; this reads as a pale wall while
        // staying distinct from both the backdrop (#fff → #edeeed) and the
        // warmer floor (#d1d5db). The 30% faded near wall reads lighter still.
        case 'wall':    return { opacity: 1, colorOverride: '#dde0e4' };
        case 'door':    return { opacity: 1, colorOverride: '#78350f' };
        case 'window':  return { opacity: 0.5, colorOverride: '#60a5fa' }; // glassy; 0.5 stays visible at oblique angles (was 0.3 / #93c5fd)
        case 'floor':   return { opacity: 1, colorOverride: '#d1d5db' };   // gray-300 — sits lighter than walls
        // Doorless passages: the wall cuts a real hole here, so the panel
        // is a faint amber tint over the gap — see-through like the
        // passage it represents, but still hoverable/selectable and
        // visible enough to read as "an opening, not a missing wall".
        case 'opening': return { opacity: 0.25, colorOverride: '#92400e' };
        default:        return null; // 'object' / 'unknown' handled by caller
    }
};

// Single boundary helper: collapse the API's snake_case
// ``polygon_corners`` and RoomPlan's camelCase ``polygonCorners`` into
// one canonical field so downstream code never has to branch on casing.
// Returns a shallow copy with ``polygonCorners`` populated when either
// shape was present.
//
// Exported for direct unit testing — used to be verified indirectly
// through SceneObjectList; the boundary is the right place to pin it.
export const normalizePolygonCasing = (surface) => {
    if (!surface || typeof surface !== 'object') return surface;
    const polygon = surface.polygonCorners ?? surface.polygon_corners ?? null;
    return { ...surface, polygonCorners: polygon };
};

// Build an extruded mesh from a RoomPlan polygon outline. The corners are
// in the wall's local X-Y plane (Z ≈ 0 and ignored); the parent <group>
// applies the wall's transform to position the slab in world space. The
// extrusion is centered on the plane so the slab sits symmetrically where
// the original zero-thickness quad would have been.
//
// `holes` (optional) is a list of {minX, maxX, minY, maxY} rects in the same
// local plane (from computeWallHoles) cut out of the slab — door/window/
// opening cut-outs. ExtrudeGeometry normalizes hole winding itself, so the
// rect corner order doesn't matter. If hole triangulation ever fails on
// pathological scan data (overlapping holes, hole crossing a sloped contour
// edge), fall back to the solid slab — a missing cut-out beats a crashed
// viewer.
//
// Returns null when the polygon is degenerate, signaling the caller to
// fall back to the bounding-box renderer.
//
// Exported for unit testing — the React tree mocks Three.js, so the only
// way to verify polygon vs box selection is to call this directly.
export const buildPolygonGeometry = (polygonCorners, thickness, holes = []) => {
    if (!Array.isArray(polygonCorners) || polygonCorners.length < 3) {
        return null;
    }
    const buildShape = (withHoles) => {
        const shape = new THREE.Shape();
        shape.moveTo(polygonCorners[0][0], polygonCorners[0][1]);
        for (let i = 1; i < polygonCorners.length; i++) {
            shape.lineTo(polygonCorners[i][0], polygonCorners[i][1]);
        }
        shape.closePath();
        if (withHoles) {
            for (const rect of holes) {
                const path = new THREE.Path();
                path.moveTo(rect.minX, rect.minY);
                path.lineTo(rect.maxX, rect.minY);
                path.lineTo(rect.maxX, rect.maxY);
                path.lineTo(rect.minX, rect.maxY);
                path.closePath();
                shape.holes.push(path);
            }
        }
        return shape;
    };
    const extrude = (shape) => {
        const geometry = new THREE.ExtrudeGeometry(shape, {
            depth: thickness,
            bevelEnabled: false,
        });
        // Center the extrusion on the local plane (extrude defaults to 0..depth).
        geometry.translate(0, 0, -thickness / 2);
        return geometry;
    };
    if (Array.isArray(holes) && holes.length > 0) {
        try {
            return extrude(buildShape(true));
        } catch (e) {
            console.warn('buildPolygonGeometry: hole triangulation failed, rendering solid surface', e);
        }
    }
    return extrude(buildShape(false));
};

// Helper: build a THREE.Matrix4 from the API's transform shapes — a flat
// 16-element column-major array (RoomPlan), a {columns: [[...]]} object,
// or a nested array. Unrecognized input yields the identity matrix.
const matrixFromTransform = (transformInput) => {
    const matrix = new THREE.Matrix4();

    let flatArray = transformInput;
    if (transformInput && transformInput.columns) {
        flatArray = transformInput.columns.flat();
    } else if (Array.isArray(transformInput) && Array.isArray(transformInput[0])) {
        // Handle potential nested array without "columns" key if that case exists
        flatArray = transformInput.flat();
    }

    if (Array.isArray(flatArray) && flatArray.length === 16) {
        matrix.fromArray(flatArray);
    }

    return matrix;
};

// Helper: Decompose transform matrix
const decomposeTransform = (transformInput) => {
    const matrix = matrixFromTransform(transformInput);
    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    matrix.decompose(position, quaternion, scale);
    return { position, quaternion, scale };
};

// ---- Wall cut-outs ---------------------------------------------------------
// Real walls have holes where their doors/windows/openings are. Walls render
// as opaque slabs (PR #230), which swallowed the door/window/opening panels
// that RoomPlan places IN the wall plane (panel thickness 6cm nests inside
// the 8cm wall) — embedded panels were invisible except on the one wall the
// dollhouse culler fades. Cutting the child's rectangle out of the wall
// geometry makes every opening readable from any angle and either side:
// doorless passages become see-through gaps, doors/windows sit visibly
// inside their hole.

// Holes must sit STRICTLY inside the wall outline. RoomPlan doors are flush
// with the wall's bottom edge (door minY === wall minY exactly), and a hole
// edge coincident with the contour is a degenerate input for earcut
// triangulation. Clamping every hole 2mm inside the outline bounds leaves an
// invisible sliver of wall and a well-conditioned triangulation.
const HOLE_INSET_M = 0.002;
// A hole that clamps down to under 1cm in either axis is scan noise (or a
// child that barely overlaps its parent) — skip it rather than cut a sliver.
const MIN_HOLE_SIZE_M = 0.01;

// Project each child opening (door/window/opening) into its parent wall's
// local plane and return axis-aligned hole rects {minX, maxX, minY, maxY}.
// The child's world position is mapped through the inverse wall transform;
// its dimensions[0]/[1] give the rect extents. RoomPlan children lie exactly
// in the wall plane and share (or 180°-flip) the wall's rotation, so an
// axis-aligned rect centered on the projected point is exact — a flipped
// child only flips the local X sign, which a centered rect doesn't care
// about.
//
// `outline` is the wall's polygon in local coords (explicit polygonCorners
// or a rectangle synthesized from dimensions); its bounding box is the
// clamping envelope. Children with malformed transforms/dimensions are
// skipped, never thrown on — a missing hole degrades to a solid wall.
//
// Exported for unit testing (pure math, no Canvas needed).
export const computeWallHoles = (wall, children, outline) => {
    if (!wall?.transform || !Array.isArray(children) || children.length === 0) return [];
    if (!Array.isArray(outline) || outline.length < 3) return [];

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const corner of outline) {
        if (!Array.isArray(corner) || corner.length < 2) return [];
        const [cx, cy] = corner;
        if (!Number.isFinite(cx) || !Number.isFinite(cy)) return [];
        if (cx < minX) minX = cx;
        if (cx > maxX) maxX = cx;
        if (cy < minY) minY = cy;
        if (cy > maxY) maxY = cy;
    }

    const wallInverse = matrixFromTransform(wall.transform).invert();

    const holes = [];
    for (const child of children) {
        const dims = child?.dimensions;
        if (!child?.transform || !Array.isArray(dims) || dims.length < 2) continue;
        const [w, h] = dims;
        if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) continue;

        const local = new THREE.Vector3()
            .setFromMatrixPosition(matrixFromTransform(child.transform))
            .applyMatrix4(wallInverse);

        const rect = {
            minX: Math.max(local.x - w / 2, minX + HOLE_INSET_M),
            maxX: Math.min(local.x + w / 2, maxX - HOLE_INSET_M),
            minY: Math.max(local.y - h / 2, minY + HOLE_INSET_M),
            maxY: Math.min(local.y + h / 2, maxY - HOLE_INSET_M),
        };
        if (rect.maxX - rect.minX < MIN_HOLE_SIZE_M) continue;
        if (rect.maxY - rect.minY < MIN_HOLE_SIZE_M) continue;
        holes.push(rect);
    }
    return holes;
};

// Resolve a wall's render geometry inputs: the polygon outline it extrudes
// and the hole rects its visible children cut out of it.
//
// INVARIANT — a wall's render MODE (polygon-extrusion vs box-fallback) must
// never change after mount. The outline is attached whenever it is
// derivable (explicit polygonCorners, else a rectangle synthesized from
// dimensions), NOT only when holes exist. An earlier version returned the
// outline only for holed walls; hiding a wall's last door/window/opening
// then flipped the mounted <mesh> from geometry-prop mode to
// scale+boxGeometry mode in place, and R3F's prop-removal left the mesh
// with `geometry === undefined` — which crashed BOTH the render loop
// (Frustum.intersectsObject) and pointer raycasting (Mesh.raycast) on
// every frame/event. With the outline always present, a hole change is
// just a new ExtrudeGeometry in the same mode — a safe update.
//
// Walls with no derivable outline (no corners AND invalid dimensions) stay
// in box mode forever — also stable, since computeWallHoles requires the
// outline, so they can never gain holes.
//
// Exported for unit testing (the mode-stability invariant is pinned there).
export const buildWallRenderEntry = (wall, visibleChildren) => {
    const explicit = wall?.polygonCorners;
    const outline = (Array.isArray(explicit) && explicit.length >= 3)
        ? explicit
        : inferRectanglePolygon(wall?.dimensions);
    const holes = (outline && Array.isArray(visibleChildren) && visibleChildren.length > 0)
        ? computeWallHoles(wall, visibleChildren, outline)
        : [];
    return {
        polygonCorners: outline ?? wall?.polygonCorners ?? null,
        holes,
    };
};

// Dollhouse culling math. A wall is extruded along its local +Z
// (buildPolygonGeometry); rotated by the wall's quaternion this gives the
// wall's facing axis in world space. Empirically (verified in the dev
// server against a real RoomPlan scan), RoomPlan's local +Z points toward
// the room INTERIOR, so the near wall between the camera and the room has
// its +Z pointing the same way the camera looks into the room — i.e. the
// same direction as camera→wall. We hide that near wall so the interior is
// visible (dollhouse view), keeping the far walls solid as a backdrop.
//
// So: hide when `normal · (wallPos - cameraPos) > 0` (the wall's +Z points
// away from the camera, into the room). Perpendicular / edge-on walls
// (dot ≈ 0) are KEPT — they don't occlude the interior. A small positive
// epsilon keeps grazing-angle walls from flip-flopping on floating-point
// noise (a 90°-rotated edge-on normal lands at ~±2e-16).
//
// Exported as a pure function so the rule is unit-testable without a
// render loop (the Canvas is mocked in tests). Inputs are plain THREE objects.
const FACING_EPSILON = 1e-6;
export const isWallFacingCamera = (wallQuaternion, wallPos, cameraPos, threshold = FACING_EPSILON) => {
    if (!wallQuaternion || !wallPos || !cameraPos) return false;
    const normal = new THREE.Vector3(0, 0, 1).applyQuaternion(wallQuaternion).normalize();
    const toWall = new THREE.Vector3().subVectors(wallPos, cameraPos).normalize();
    // normal·toWall > ~0  ⇒ the wall's +Z points away from the camera (into
    // the room) ⇒ this is the near occluding wall ⇒ hide it.
    return normal.dot(toWall) > threshold;
};

// Compute a single "room center" point for the 1st-person camera preset.
// Averages the world positions of floor surfaces (the most reliable proxy
// for where a room's interior is); falls back to wall positions when there
// are no floors, and returns null when there's nothing to average. The
// caller places the eye-level camera at {x, <eye height>, z} of this point.
//
// Exported for unit testing — reuses decomposeTransform so it stays in
// lockstep with how meshes are actually positioned.
export const computeRoomCenter = (floors, walls) => {
    const source = (Array.isArray(floors) && floors.length > 0)
        ? floors
        : (Array.isArray(walls) ? walls : []);
    if (source.length === 0) return null;
    const sum = new THREE.Vector3();
    let count = 0;
    for (const surface of source) {
        if (!surface || !surface.transform) continue;
        const { position } = decomposeTransform(surface.transform);
        sum.add(position);
        count += 1;
    }
    if (count === 0) return null;
    sum.multiplyScalar(1 / count);
    return { x: sum.x, y: sum.y, z: sum.z };
};


// Small reusable label chip at a 3D point, rendered via drei's <Html> —
// DOM labels stay readable from any camera angle and inherit the page's
// theme tokens via CSS variables. Pointer events are blocked so labels
// never steal clicks from the mesh underneath (selection/hover still work).
// Labels render on top of scene geometry (no z-cull) — drei's `<Html
// occlude>` opt-in isn't used; the walls are translucent enough that
// label-through-wall isn't a UX problem today. The SINGLE chip styling for
// every measurement in the scene (edge, gap, net area, object dimensions)
// so they read as one system.
const MeasurementChip = ({ position, text, testid }) => (
    <Html
        position={position}
        center
        style={{ pointerEvents: 'none', userSelect: 'none' }}
    >
        <div
            data-testid={testid}
            className="px-1.5 py-0.5 text-[10px] font-medium rounded shadow-sm whitespace-nowrap bg-[var(--bg-card)] text-[var(--text-main)] border border-[var(--border)]"
        >
            {text}
        </div>
    </Html>
);

// ---- World-axis gizmo ------------------------------------------------------
// Shown on the selected surface and at the floor-plan editor's walk origin.
// drei <Line> (Line2 under the hood) because native WebGL lines ignore
// linewidth — the 5px width is the point; the old 1px axesHelper was
// unreadable. Colors are shared with the editor panel's nudge controls
// (AXIS_COLORS), so pressing the cyan arrow visibly means "move along the
// cyan line". X/Z span both directions (nudges go both ways); Y points up
// only, muted, as a vertical reference (height isn't nudgeable). Raycast is
// disabled on every line so the gizmo never steals clicks from surfaces.

const AXIS_GIZMO_SPAN_M = 1.2;
const AXIS_GIZMO_LINE_WIDTH_PX = 5;

const AxisGizmoLabel = ({ position, color, text }) => (
    <Html position={position} center style={{ pointerEvents: 'none', userSelect: 'none' }}>
        <div
            data-testid="axis-gizmo-label"
            className="px-1 text-[10px] font-bold rounded shadow-sm bg-[var(--bg-card)] border border-[var(--border)]"
            style={{ color }}
        >
            {text}
        </div>
    </Html>
);

const AxisGizmo = () => (
    <group>
        <Line
            points={[[-AXIS_GIZMO_SPAN_M, 0, 0], [AXIS_GIZMO_SPAN_M, 0, 0]]}
            color={AXIS_COLORS.x}
            lineWidth={AXIS_GIZMO_LINE_WIDTH_PX}
            raycast={() => null}
        />
        <Line
            points={[[0, 0, -AXIS_GIZMO_SPAN_M], [0, 0, AXIS_GIZMO_SPAN_M]]}
            color={AXIS_COLORS.z}
            lineWidth={AXIS_GIZMO_LINE_WIDTH_PX}
            raycast={() => null}
        />
        <Line
            points={[[0, 0, 0], [0, AXIS_GIZMO_SPAN_M, 0]]}
            color={AXIS_COLORS.y}
            lineWidth={AXIS_GIZMO_LINE_WIDTH_PX}
            raycast={() => null}
        />
        <AxisGizmoLabel position={[AXIS_GIZMO_SPAN_M + 0.18, 0, 0]} color={AXIS_COLORS.x} text="X" />
        <AxisGizmoLabel position={[0, 0, AXIS_GIZMO_SPAN_M + 0.18]} color={AXIS_COLORS.z} text="Z" />
        <AxisGizmoLabel position={[0, AXIS_GIZMO_SPAN_M + 0.18, 0]} color={AXIS_COLORS.y} text="Y" />
    </group>
);


// Render small floating labels at the midpoint of each edge of a wall's
// polygon outline. The polygon corners are in the wall's local plane; the
// parent <group> applies the wall's transform so labels track the wall in
// world space.
const WallEdgeLabels = ({ polygonCorners, transform, unitSystem }) => {
    const edges = useMemo(() => classifyWallEdges(polygonCorners), [polygonCorners]);
    const { position, quaternion } = useMemo(() => decomposeTransform(transform), [transform]);

    if (!edges) return null;

    const suffix = getUnitSystemSuffix(unitSystem);
    const fmt = (m) => {
        const v = convertMetersValue(m, unitSystem);
        return v >= 10 ? v.toFixed(0) : v.toFixed(1);
    };

    return (
        <group position={position} quaternion={quaternion}>
            {edges.map((edge, idx) => {
                const lenStr = `${fmt(edge.length)}${suffix}`;
                const text = edge.direction === 'sloped'
                    ? `${edge.label} ${lenStr} (${Math.round(edge.angleDeg)}°)`
                    : `${edge.label} ${lenStr}`;
                return (
                    <MeasurementChip
                        key={idx}
                        testid="wall-edge-label"
                        position={[edge.midpoint[0], edge.midpoint[1], 0]}
                        text={text}
                    />
                );
            })}
        </group>
    );
};

// Derived spacing labels for one wall: the gaps between its openings and the
// distance from the end openings to the wall corners (from the backend
// `wall_measurements` block), plus the net (paintable) area on the wall face.
// Positioned in the wall's local plane inside its transform group, exactly
// like WallEdgeLabels — `alongM` maps to local X via `alongM − wallWidth/2`.
const OpeningMeasurementLabels = ({ measurement, dimensions, transform, unitSystem }) => {
    const { position, quaternion } = useMemo(() => decomposeTransform(transform), [transform]);
    const wallWidth = Array.isArray(dimensions) ? dimensions[0] : undefined;
    const spacingLabels = useMemo(
        () => buildOpeningSpacingLabels(measurement?.openings, wallWidth, unitSystem),
        [measurement, wallWidth, unitSystem]
    );

    if (!measurement || !Number.isFinite(wallWidth)) return null;

    const netArea = measurement.net_area_m2;

    return (
        <group position={position} quaternion={quaternion}>
            {spacingLabels.map((label) => (
                <MeasurementChip
                    key={label.key}
                    testid="opening-spacing-label"
                    // Spacing labels sit at the wall's vertical mid-line; the
                    // edge labels live at the wall's borders, so they don't
                    // collide.
                    position={[label.alongM - wallWidth / 2, 0, 0.03]}
                    text={label.text}
                />
            ))}
            {typeof netArea === 'number' && (
                <MeasurementChip
                    testid="wall-net-area-label"
                    // Net-area chip near the top of the wall face, clear of the
                    // spacing labels on the mid-line. Guard the height axis:
                    // a short dimensions array would put NaN in the position.
                    position={[0, Number.isFinite(dimensions?.[1]) ? dimensions[1] / 2 - 0.25 : 0, 0.03]}
                    text={`Net ${formatArea(netArea, unitSystem)}`}
                />
            )}
        </group>
    );
};

// A single dimension label (W × H × D) centered on a captured furniture
// object, so "measurements for other objects" read the same way walls do.
// The object's transform group places the chip at its center in world space.
const ObjectMeasurementLabels = ({ dimensions, transform, unitSystem }) => {
    const { position, quaternion } = useMemo(() => decomposeTransform(transform), [transform]);
    if (!Array.isArray(dimensions) || dimensions.length < 2) return null;
    return (
        <group position={position} quaternion={quaternion}>
            <MeasurementChip
                testid="object-measurement-label"
                position={[0, 0, 0]}
                text={formatDimensionTriplet(dimensions, unitSystem)}
            />
        </group>
    );
};

// Component to render captured objects with optimization (LOD + Scale)
const CapturedObjectMesh = ({ data, selectedId, onSelect, colorOverride, opacity = 1, suppressSelectionGizmo = false }) => {
    const { identifier, transform, dimensions, category, renderType } = data;
    // Ghost surfaces (the editor's not-yet-added wall preview) are pure
    // visual feedback: never selectable, hoverable, or raycast targets.
    const isGhost = !!data.ghost;
    const isSelected = !isGhost && selectedId === identifier;
    const [hovered, setHovered] = useState(false);

    // Memorize geometry props
    const { position, quaternion } = useMemo(() => decomposeTransform(transform), [transform]);

    // The editor's walk-origin marker carries this flag so the world-axis
    // gizmo shows where the nudge arrows will move the run — even before a
    // wall exists and without needing a selection.
    const isWalkOrigin = !!data.attributes?.walkOrigin;
    // The gizmo must show WORLD axes (what the nudge arrows move along), but
    // this group carries the surface's rotation — a local-axes gizmo on a
    // rotated scanned wall points its "Z" line along the wall, which is
    // exactly the up/down-arrow confusion the gizmo exists to kill. Counter-
    // rotate by the inverse quaternion so the lines stay world-aligned.
    const worldAlignQuaternion = useMemo(() => quaternion.clone().invert(), [quaternion]);

    // Polygon-extrusion path: when a wall or floor has a non-empty
    // polygonCorners outline, render its actual shape. RoomPlan reports
    // the bounding rectangle in `dimensions` but the true outline (e.g. a
    // pentagon with a sloped edge under a staircase) in `polygonCorners`.
    // Rendering the bounding box draws geometry where the wall isn't,
    // which gets occluded by adjacent objects and appears missing.
    //
    // Casing normalization happens upstream in `normalizePolygonCasing`
    // — by the time data reaches this component, `polygonCorners` is
    // the canonical field regardless of which casing the API used.
    const polygonGeometry = useMemo(() => {
        if (renderType !== 'wall' && renderType !== 'floor') return null;
        const thickness = renderType === 'wall'
            ? resolveWallExtrusionThickness(dimensions)
            : FLOOR_THICKNESS_M;
        // `holes` carries door/window/opening cut-outs (walls only — floors
        // never have them); attached upstream in allRenderObjects.
        return buildPolygonGeometry(data.polygonCorners, thickness, data.holes || []);
    }, [renderType, dimensions, data.polygonCorners, data.holes]);

    // Box-fallback dimensions. RoomPlan walls/doors/windows have
    // dimensions[2] === 0 (2D quads — thickness implied); inject a
    // visible thickness so the box doesn't collapse to a plane.
    const dims = useMemo(
        () => resolveOpeningThickness(renderType, dimensions),
        [dimensions, renderType]
    );

    // Dispose any ExtrudeGeometry we created to avoid GPU memory leaks
    // on unmount or when polygonGeometry changes identity.
    useEffect(() => {
        return () => {
            if (polygonGeometry) polygonGeometry.dispose();
        };
    }, [polygonGeometry]);

    // Color logic
    const color = colorOverride || getCategoryColor(category);

    const displayColor = isGhost ? color : (isSelected ? '#ef4444' : hovered ? '#f87171' : color);

    // Cleanup cursor style on unmount to prevent memory leak
    useEffect(() => {
        return () => {
            // Reset cursor if this component was being hovered when unmounted
            if (hovered) {
                document.body.style.cursor = 'auto';
            }
        };
    }, [hovered]);

    // Handler wrappers
    const handleClick = (e) => { e.stopPropagation(); onSelect(identifier); };
    const handleOver = (e) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = 'pointer'; };
    const handleOut = () => { setHovered(false); document.body.style.cursor = 'auto'; };

    // DoubleSide on planar surfaces is a belt-and-braces guard: even after
    // injecting thickness, near-coplanar walls/openings can z-fight; rendering
    // both faces means the slab is visible from any angle.
    const isPlanar = renderType === 'wall' || renderType === 'door' ||
                     renderType === 'window' || renderType === 'opening' ||
                     renderType === 'floor';

    return (
        <group
            position={position}
            quaternion={quaternion}
            // Tag wall groups so WallCuller can find them via scene.traverse
            // and hide whichever wall faces the camera (dollhouse view). The
            // group carries the wall's world transform, so its own quaternion
            // gives the outward normal. Only walls are tagged.
            userData={renderType === 'wall' ? { isWall: true, identifier } : undefined}
        >
            {/* LOD Wrapper: Prepare for future high-poly models */}
            {/* Currently using BoxGeometry for all levels, but structure is ready */}
            {/*
                Each LOD level renders ONE of two keyed branches:
                - "poly": geometry prop set to the extruded polygon (already in
                  world units — no scale)
                - "box": unit boxGeometry child scaled by dims
                The distinct keys are load-bearing. A single <mesh> that swaps
                between the two prop shapes in place (geometry added/removed,
                scale added/removed, boxGeometry child mounted/unmounted) ends
                up with `geometry === undefined` after R3F's prop-removal +
                child-detach dance — which crashes the render loop
                (Frustum.intersectsObject) and pointer raycasting (Mesh.raycast)
                on every frame/event. Keys force a clean unmount/remount on any
                mode change instead.
            */}
            <Detailed distances={[0, 20]}>
                {/* High Detail (Level 0) */}
                {polygonGeometry ? (
                    <mesh
                        key="hi-poly"
                        geometry={polygonGeometry}
                        onClick={isGhost ? undefined : handleClick}
                        onPointerOver={isGhost ? undefined : handleOver}
                        onPointerOut={isGhost ? undefined : handleOut}
                        // No handlers already keeps ghosts out of R3F's pointer
                        // raycasting; the no-op raycast guarantees they can never
                        // occlude clicks aimed at real geometry behind them.
                        raycast={isGhost ? () => null : undefined}
                    >
                        <meshStandardMaterial
                            color={displayColor}
                            transparent={opacity < 1}
                            opacity={opacity}
                            side={isPlanar ? THREE.DoubleSide : THREE.FrontSide}
                        />
                    </mesh>
                ) : (
                    <mesh
                        key="hi-box"
                        scale={dims}
                        onClick={isGhost ? undefined : handleClick}
                        onPointerOver={isGhost ? undefined : handleOver}
                        onPointerOut={isGhost ? undefined : handleOut}
                        raycast={isGhost ? () => null : undefined}
                    >
                        <boxGeometry args={[1, 1, 1]} />
                        <meshStandardMaterial
                            color={displayColor}
                            transparent={opacity < 1}
                            opacity={opacity}
                            side={isPlanar ? THREE.DoubleSide : THREE.FrontSide}
                        />
                    </mesh>
                )}

                {/* Far Detail (Level 1, camera beyond ~20m).
                    THREE.LOD.update() force-toggles `visible` on whichever
                    level is active EVERY frame — a `visible={false}`
                    placeholder here doesn't stay hidden, it shows the moment
                    the camera crosses the distance threshold (the old
                    wireframe placeholder leaked as a glitchy wireframe room
                    on zoom-out, with walls popping level-by-level as each
                    crossed 20m). So level 1 must always be a real,
                    presentable stand-in. Same geometry, flat UNLIT material:
                    meshBasicMaterial skips the PBR lighting pass entirely,
                    which is the actual LOD savings at range where shading
                    nuance is imperceptible. The LOD scaffold stays so future
                    high-poly models / Gaussian splats can hang true
                    reduced-detail proxies on this level. Handlers mirror the
                    near mesh so hover/selection keep working at any zoom. */}
                {polygonGeometry ? (
                    <mesh
                        key="med-poly"
                        geometry={polygonGeometry}
                        onClick={isGhost ? undefined : handleClick}
                        onPointerOver={isGhost ? undefined : handleOver}
                        onPointerOut={isGhost ? undefined : handleOut}
                        raycast={isGhost ? () => null : undefined}
                    >
                        <meshBasicMaterial
                            color={displayColor}
                            transparent={opacity < 1}
                            opacity={opacity}
                            side={isPlanar ? THREE.DoubleSide : THREE.FrontSide}
                        />
                    </mesh>
                ) : (
                    <mesh
                        key="med-box"
                        scale={dims}
                        onClick={isGhost ? undefined : handleClick}
                        onPointerOver={isGhost ? undefined : handleOver}
                        onPointerOut={isGhost ? undefined : handleOut}
                        raycast={isGhost ? () => null : undefined}
                    >
                        <boxGeometry args={[1, 1, 1]} />
                        <meshBasicMaterial
                            color={displayColor}
                            transparent={opacity < 1}
                            opacity={opacity}
                            side={isPlanar ? THREE.DoubleSide : THREE.FrontSide}
                        />
                    </mesh>
                )}
            </Detailed>
            {/* Axis gizmo lives on the GROUP (position/quaternion only),
                never inside the box-mode mesh — a mesh scaled by dims would
                stretch the axes by the panel's [w, h, depth] (a selected
                door drew a 2m green spike). Two mounting modes:
                - Selected surface (scanned-room browsing): counter-rotated
                  to WORLD axes, and suppressed entirely while the editor's
                  marker ghosts are up (they carry the frame gizmo instead).
                - Walk-origin / chosen-corner markers: rendered in the
                  marker's LOCAL frame — the marker transform bakes the
                  starter frame rotation, so the X/Z lines and labels spin
                  live with the panel's rotation field and show the exact
                  directions the nudge arrows move along. */}
            {isSelected && !suppressSelectionGizmo && !isWalkOrigin && (
                <group quaternion={worldAlignQuaternion}>
                    <AxisGizmo />
                </group>
            )}
            {isWalkOrigin && <AxisGizmo />}
        </group>
    );
};


// Dollhouse wall-culling. A null-rendering child placed inside <Canvas> so
// it can read the live camera each frame via useThree (mirrors the
// AutoRotate pattern in Model3DViewer). Each frame, in 3D mode, it hides
// whichever wall faces the camera so the near wall never occludes the room
// interior; rotating the view re-evaluates, so the newly-front wall hides
// and the previously-front wall reappears.
//
// Walls hidden via the per-object visibility set (hiddenIds) are filtered
// out of the render list upstream, so they aren't in the scene at all — the
// culler only ever sees currently-visible walls and never resurrects a
// user-hidden one.
// Opacity applied to a camera-facing wall. Rather than hiding the near wall
// outright, fade it to 30% so the user sees INTO the room while the wall
// itself stays faintly visible (its outline still reads). Far walls stay
// fully solid as a backdrop.
const FACING_WALL_OPACITY = 0.3;

// Set every mesh material under a wall group to a target opacity. meshStandard-
// Material only honors `opacity` when `transparent` is true, so toggle that in
// step. Handles single materials and material arrays.
const setWallGroupOpacity = (group, opacity) => {
    const transparent = opacity < 1;
    group.traverse((child) => {
        if (!child.material) return;
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        for (const mat of materials) {
            mat.opacity = opacity;
            mat.transparent = transparent;
        }
    });
};

const WallCuller = ({ active }) => {
    const { camera, scene } = useThree();
    const wallWorldQuat = useRef(new THREE.Quaternion());
    const wallWorldPos = useRef(new THREE.Vector3());

    useFrame(() => {
        scene.traverse((obj) => {
            if (!obj.userData?.isWall) return;
            if (!active) {
                // 2D / culling-off: every wall fully solid.
                setWallGroupOpacity(obj, 1);
                return;
            }
            obj.getWorldQuaternion(wallWorldQuat.current);
            obj.getWorldPosition(wallWorldPos.current);
            const facing = isWallFacingCamera(
                wallWorldQuat.current,
                wallWorldPos.current,
                camera.position
            );
            // Near (camera-facing) wall → 30% so you can see in but still see
            // the wall; far walls → solid.
            setWallGroupOpacity(obj, facing ? FACING_WALL_OPACITY : 1);
        });
    });

    return null;
};

// Camera-preset rig. Also a null-rendering child inside <Canvas>. When the
// preset changes it imperatively moves the camera + OrbitControls target;
// it does NOT run every frame (that would fight the user's orbiting), only
// on a preset transition, tracked via a ref.
//  - '3rd': elevated overview angle, looking at the room center near floor.
//  - '1st': low eye-level (~0.8m) at the room centroid, looking horizontally
//           out so OrbitControls orbits around the viewer's head. Tuned down
//           from standing height — the scans read better from a lower vantage.
//  - 'top': handled by the OrthographicCamera path; the rig no-ops.
const EYE_HEIGHT_M = 0.8;

// A stable identity for a (preset, center) pair. The rig re-applies whenever
// this key changes — so it fires once per preset switch AND once when the
// room centroid first becomes available (null → real center). Without the
// center in the key, the rig latches on the first frame (while roomCenter is
// still null because scan data hasn't loaded) and then never re-frames the
// room once the data arrives — the default '3rd' view would stay pointed at
// world origin instead of the room.
export const cameraPresetKey = (preset, roomCenter) => {
    const c = roomCenter || { x: 0, y: 0, z: 0 };
    return `${preset}:${c.x},${c.y},${c.z}`;
};

// Pure camera-placement math for a preset, extracted so it can be unit-tested
// (the rig's useFrame body never runs under the mocked Canvas). Mutates the
// passed camera/controls; returns nothing. 'top' is a no-op here — the
// orthographic camera path handles top-down framing.
export const applyCameraPreset = (preset, roomCenter, camera, controls) => {
    const center = roomCenter || { x: 0, y: 0, z: 0 };
    if (preset === '3rd') {
        camera.position.set(center.x + 5, 10, center.z + 10);
        if (controls) controls.target.set(center.x, 0, center.z);
        camera.lookAt(center.x, 0, center.z);
    } else if (preset === '1st') {
        camera.position.set(center.x, EYE_HEIGHT_M, center.z);
        // Aim at a point one unit horizontally out so OrbitControls
        // orbits around the eye, not a far-off floor point.
        const target = { x: center.x + 1, y: EYE_HEIGHT_M, z: center.z };
        if (controls) controls.target.set(target.x, target.y, target.z);
        camera.lookAt(target.x, target.y, target.z);
    } else {
        return; // 'top' — nothing to do.
    }
    if (controls) controls.update();
};

const CameraRig = ({ preset, roomCenter, controlsRef }) => {
    const { camera } = useThree();
    const appliedKeyRef = useRef(null);

    useFrame(() => {
        // Key on (preset, center) so a centroid that arrives AFTER mount
        // (scan data loads async) re-frames the room exactly once, instead
        // of the rig latching on the null-center first frame forever.
        const key = cameraPresetKey(preset, roomCenter);
        if (appliedKeyRef.current === key) return;
        applyCameraPreset(preset, roomCenter, camera, controlsRef.current);
        appliedKeyRef.current = key;
    });

    return null;
};


/**
 * Main FloorPlanViewer component
 *
 * @param {Object} props
 * @param {string} props.roomId - Room ID for loading scan data
 * @param {Object|null} props.previewData - When set, render this data instead
 *   of fetching by roomId. Shape-identical to the getRoomScan response
 *   ({data: {walls, doors, ...}, floor_area, ...}). Used for user-created
 *   floor plans (saved plans AND the editor's live preview) so the viewer
 *   renders scans and plans through one path.
 * @param {string} props.className - Additional CSS classes
 * @param {string|null} props.selectedId - Controlled selection state (optional)
 * @param {Function} props.onSelect - Callback when object is selected: (id) => void
 * @param {Function} props.onSceneDataLoad - Callback when scene data is loaded: ({ objects, walls, doors, windows, floors, openings, unknown, floorArea, floorAreaSource, floorFootprintM, floorSections, wallMeasurements }) => void
 * @param {Set<string>} props.hiddenIds - Scene identifiers to hide from the 3D view (per-object visibility)
 * @param {'meters'|'feet'|'inches'} props.unitSystem - Controlled unit system (optional)
 * @param {Function} props.onUnitSystemChange - Callback when unit system changes: (unitSystem) => void
 * @param {Function} props.onScanStatusChange - Reports the scanned-room fetch
 *   outcome to the parent: 'loading' | 'present' | 'empty' | 'error'. Fired only
 *   in the roomId (scanned-room) fetch path — NOT for previewData (plans) or
 *   sample data. Lets the parent render a friendly empty-state guide instead of
 *   the error card when a room has simply never been scanned.
 */
const FloorPlanViewer = ({
    roomId,
    previewData,
    className = '',
    selectedId: controlledSelectedId,
    onSelect,
    onSceneDataLoad,
    hiddenIds = EMPTY_HIDDEN_IDS,
    unitSystem: controlledUnitSystem,
    onUnitSystemChange,
    onScanStatusChange
}) => {
    // Camera preset: 'top' (orthographic top-down), '3rd' (elevated 3D
    // overview), '1st' (eye-level inside the room). `viewMode` is derived
    // from it ('top' ⇒ '2d', else '3d') and still drives the camera switch,
    // OrbitControls.enableRotate, and measurement-label gating. Default 3rd.
    const [cameraPreset, setCameraPreset] = useState('3rd');
    const viewMode = cameraPreset === 'top' ? '2d' : '3d';
    // Imperative handle to OrbitControls so CameraRig can retarget it.
    const controlsRef = useRef(null);
    // Per-edge measurement labels overlaid on each wall in the 3D scene.
    // Off by default — labels can crowd the view in dense scans; the user
    // opts in when they want to read measurements.
    const [showMeasurements, setShowMeasurements] = useState(false);
    // Ground-plane gridlines. Off by default — the grid is a scale reference
    // most users don't need at a glance and it adds visual noise; the bottom
    // toggle restores it on demand.
    const [showGrid, setShowGrid] = useState(false);
    // Internal selection state for uncontrolled mode
    const [internalSelectedId, setInternalSelectedId] = useState(null);
    const [internalUnitSystem, setInternalUnitSystem] = useState(UNIT_SYSTEMS.INCHES);
    const [scanData, setScanData] = useState(null);
    const [_loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sampleType, setSampleType] = useState('bedroom');
    // Cross-client vocabulary fetched from /api/v1/config/scene-constants.
    // Initialized to the local mirror so the first render works while the
    // fetch is in flight (and unit tests don't need to mock the call).
    // The fetched copy overrides on resolve; if the request fails, the
    // service swallows + falls back to the mirror, so this state never
    // ends up null.
    //
    // Underscore prefix marks this as "fetched, not yet consumed" — the
    // call sites (classifyWallEdges labels, UNIT_SYSTEMS references)
    // still read hardcoded copies. Migrating them is incremental
    // follow-up work; this state establishes the pattern + verifies the
    // wire shape end-to-end. Same convention as `_loading` above.
    const [_sceneConstants, setSceneConstants] = useState(DEFAULT_SCENE_CONSTANTS);

    // Determine if we're in controlled mode
    const isControlled = controlledSelectedId !== undefined;
    const selectedId = isControlled ? controlledSelectedId : internalSelectedId;
    const isUnitSystemControlled = controlledUnitSystem !== undefined;
    const unitSystem = isUnitSystemControlled
        ? normalizeUnitSystem(controlledUnitSystem)
        : internalUnitSystem;

    // Monotonic counter that supersedes in-flight loads. If previewData
    // arrives (or the room switches) while a getRoomScan fetch is still in
    // the air, the late fetch resolution must NOT clobber the newer data —
    // each loadData call claims a generation and only the latest may write.
    const loadGenerationRef = useRef(0);

    // Keep the latest onScanStatusChange in a ref so loadData doesn't need it
    // in its dependency array — otherwise an inline callback from a caller
    // would change identity every render and re-trigger the fetch in a loop.
    const onScanStatusChangeRef = useRef(onScanStatusChange);
    onScanStatusChangeRef.current = onScanStatusChange;

    // Load Data Logic with retry support
    const loadData = useCallback(async () => {
        const generation = ++loadGenerationRef.current;
        setLoading(true);
        setError(null);

        // Externally-supplied data (user floor plan or live editor preview)
        // takes priority over the roomId fetch.
        if (previewData) {
            setScanData(previewData);
            setLoading(false);
            return;
        }

        if (roomId) {
            onScanStatusChangeRef.current?.('loading');
            try {
                const data = await RoomService.getRoomScan(roomId);
                if (loadGenerationRef.current !== generation) return; // superseded
                if (data) {
                    setScanData(data);
                    onScanStatusChangeRef.current?.('present');
                } else {
                    setError('No scan data available for this room.');
                    onScanStatusChangeRef.current?.('empty');
                }
            } catch (e) {
                if (loadGenerationRef.current !== generation) return; // superseded
                console.error('Failed to load room scan:', e);
                // Clear stale data so the onSceneDataLoad effect re-fires with
                // empty scene data — otherwise the parent's sidebar keeps
                // listing the PREVIOUS source's objects (e.g. a just-deselected
                // plan's walls) behind the error card, and clicking them
                // selects nothing in the hidden canvas.
                setScanData(null);
                // RoomService.getRoomScan throws a formatted error shaped
                // { message, code, ... } (see services/RoomService.js) — there
                // is NO `.response`. Check `e.code` first; keep `e.response`
                // as a fallback for any caller that passes a raw axios error.
                const isNotFound = e.code === 404 || e.response?.status === 404;
                setError(
                    isNotFound
                        ? 'Room scan not found. The room may not have been scanned yet.'
                        : 'Failed to load room scan. Please try again.'
                );
                onScanStatusChangeRef.current?.(isNotFound ? 'empty' : 'error');
            } finally {
                if (loadGenerationRef.current === generation) setLoading(false);
            }
        } else {
            // Load CAPTURED Sample Data
            setTimeout(() => {
                // Same superseded-check as the fetch path: a pending sample
                // timer must not clobber previewData (or a room fetch) that
                // arrived while it was waiting.
                if (loadGenerationRef.current !== generation) return;
                if (sampleType === 'bedroom') setScanData(bedroomCaptured);
                else if (sampleType === 'kitchen') setScanData(kitchenCaptured);
                else if (sampleType === 'multi') setScanData(multiRoomCaptured);
                setLoading(false);
            }, 100);
        }
    }, [roomId, sampleType, previewData]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Fetch the cross-client scene vocabulary once per page mount. The
    // service falls back to DEFAULT_SCENE_CONSTANTS on failure, so this
    // never sets state to null. Per-page fetch (vs app-boot fetch) was a
    // deliberate trade — keeps boot fast and the dependency local.
    useEffect(() => {
        let cancelled = false;
        ConfigService.getSceneConstants().then(constants => {
            if (!cancelled) setSceneConstants(constants);
        });
        return () => { cancelled = true; };
    }, []);

    const handleSelect = useCallback((id) => {
        const newSelection = selectedId === id ? null : id;

        if (isControlled) {
            // In controlled mode, just call the callback
            if (onSelect) onSelect(newSelection);
        } else {
            // In uncontrolled mode, update internal state
            setInternalSelectedId(newSelection);
        }
    }, [selectedId, isControlled, onSelect]);

    const handleUnitSystemChange = useCallback((nextUnitSystem) => {
        const normalizedUnitSystem = normalizeUnitSystem(nextUnitSystem);

        if (!isUnitSystemControlled) {
            setInternalUnitSystem(normalizedUnitSystem);
        }

        if (onUnitSystemChange) {
            onUnitSystemChange(normalizedUnitSystem);
        }
    }, [isUnitSystemControlled, onUnitSystemChange]);

    // Parse Data
    // Each list is wrapped in useMemo so the `|| []` fallback doesn't create
    // a fresh array reference on every render. Without this, the dependent
    // useMemo at allRenderObjects (and the useEffect at sceneDataLoad below)
    // would invalidate every render.
    const rawData = scanData?.data || scanData || {};
    const objects = useMemo(() => rawData.objects || [], [rawData.objects]);
    const doors = useMemo(() => rawData.doors || [], [rawData.doors]);
    const windows = useMemo(() => rawData.windows || [], [rawData.windows]);
    // Normalize the polygon casing at the boundary: RoomPlan ships
    // camelCase (polygonCorners); the normalized backend response uses
    // snake_case (polygon_corners). Doing this once here means every
    // downstream renderer + SceneObjectList sees a single canonical
    // field name, removing the `polygonCorners || polygon_corners`
    // fallback that used to live in three places.
    const walls = useMemo(
        () => (rawData.walls || []).map(normalizePolygonCasing),
        [rawData.walls]
    );
    const floors = useMemo(
        () => (rawData.floors || []).map(normalizePolygonCasing),
        [rawData.floors]
    );
    const openings = useMemo(() => rawData.openings || [], [rawData.openings]);
    // Single interior point for the 1st-person camera preset: the centroid
    // of floor positions (fallback: walls). Null when there's no geometry.
    const roomCenter = useMemo(() => computeRoomCenter(floors, walls), [floors, walls]);
    // Ghost surfaces: the floor-plan editor's preview of the wall the user
    // is ABOUT to add (translucent, non-interactive). Only ever present on
    // editor preview payloads — saved plans and scans never carry the key.
    const ghosts = useMemo(() => rawData.ghosts || [], [rawData.ghosts]);
    // Editor marker ghosts (walk-origin / anchor corners) carry the frame
    // gizmo — while any are present, selected surfaces don't mount their
    // own center gizmo, so exactly one gizmo is ever on screen.
    const editorMarkersActive = useMemo(
        () => ghosts.some((g) => g?.attributes?.anchorCorner || g?.attributes?.walkOrigin),
        [ghosts],
    );
    // `unknown` lives only on the top-level API response (computed by the
    // backend from the raw blob), never inside `data` itself. Sample
    // fixtures don't ship with it, so the `|| []` fallback keeps them
    // working unchanged.
    const unknown = useMemo(() => scanData?.unknown || [], [scanData?.unknown]);
    // Floor area is computed once on the backend (m²) and served on the
    // top-level response alongside its provenance and the true polygon
    // footprint (ADR 0006). The frontend only displays these — it never
    // recomputes area. Null/undefined on sample fixtures, which is fine.
    const floorArea = scanData?.floor_area ?? null;
    const floorAreaSource = scanData?.floor_area_source ?? null;
    const floorFootprintM = useMemo(
        () => scanData?.floor_footprint_m || null,
        [scanData?.floor_footprint_m]
    );
    // Per-section floor breakdown (one entry per scanned room/section). Lets the
    // sidebar list each section's area + a total for multi-room scans instead of
    // one collapsed number. Single-section scans yield one entry.
    const floorSections = useMemo(
        () => scanData?.floor_sections || [],
        [scanData?.floor_sections]
    );
    // Derived wall measurements keyed by wall UUID (gaps between openings,
    // wall-corner to opening, sill, and net/paintable wall area). Computed
    // server-side (services/wall_geometry.py) and only displayed here. Absent
    // on legacy responses and sample fixtures, hence the `|| {}` fallback.
    const wallMeasurements = useMemo(
        () => scanData?.wall_measurements || {},
        [scanData?.wall_measurements]
    );

    // Notify parent when scene data is available — fire exactly once per
    // distinct scanData identity. Previous implementation split this into
    // two effects (one to fire the callback, one to reset a "fired" ref
    // on scanData change). React doesn't guarantee execution order between
    // sibling effects, so on a room switch the callback effect could run
    // first, see ref=true from the previous room, skip the fire — then the
    // reset effect ran too late, and the new room's parent never got
    // notified. Single effect with a "last fired for" ref avoids the
    // race: the identity comparison happens inline before deciding to
    // fire, in the same effect body.
    const lastFiredForScanDataRef = useRef(null);

    useEffect(() => {
        // On a load error, clear the parent's sidebar exactly once: the
        // fetch failed, so whatever it was showing (e.g. a just-deselected
        // plan's walls) is now stale. `error` is the identity gate here, the
        // same way `scanData` is below.
        if (error) {
            if (onSceneDataLoad && lastFiredForScanDataRef.current !== error) {
                onSceneDataLoad({
                    objects: [], walls: [], doors: [], windows: [], floors: [],
                    openings: [], unknown: [], floorArea: null, floorAreaSource: null,
                    floorFootprintM: null, floorSections: [], wallMeasurements: {},
                });
                lastFiredForScanDataRef.current = error;
            }
            return;
        }
        // Fire once scan/plan data has LOADED — including when it's
        // legitimately empty. Gating on "has objects" instead (the old
        // behavior) meant an empty saved plan never fired the callback, so
        // the parent's sidebar kept showing stale objects from whatever
        // loaded previously (e.g. the editor's live preview) — walls that
        // no longer exist anywhere. `scanData` truthiness distinguishes
        // "loaded but empty" from "fetch still in flight" (null).
        const dataLoaded = Boolean(scanData);
        const alreadyFiredForThisData = lastFiredForScanDataRef.current === scanData;
        if (onSceneDataLoad && dataLoaded && !alreadyFiredForThisData) {
            // Extended payload — additive only. Old consumers that
            // destructured {objects, walls, doors, windows} continue to
            // work; new consumers can pick up floors/openings/unknown and
            // the backend-computed floor area/footprint.
            onSceneDataLoad({
                objects, walls, doors, windows, floors, openings, unknown,
                floorArea, floorAreaSource, floorFootprintM, floorSections,
                wallMeasurements,
            });
            lastFiredForScanDataRef.current = scanData;
        }
        // scanData is listed because it's the identity gate. objects/walls/
        // etc. are listed because they're what we pass to the callback — if
        // they change without scanData changing (shouldn't happen given the
        // useMemo wiring above, but defending the contract), we still want
        // the effect to re-evaluate.
    }, [error, scanData, objects, walls, doors, windows, floors, openings, unknown,
        floorArea, floorAreaSource, floorFootprintM, floorSections, wallMeasurements,
        onSceneDataLoad]);

    // Optimization: Combine all renderable objects into one list with pre-calculated props
    const allRenderObjects = useMemo(() => {
        const combined = [];

        // Objects
        objects.forEach(obj => {
            combined.push({
                ...obj,
                renderType: 'object',
                opacity: 1,
                // color calculated in component or can be pre-calculated here if static
            });
        });

        // Group door/window/opening children by their parent wall so each
        // wall can cut real holes where its openings sit. RoomPlan and the
        // floor-plan editor both link children via parentIdentifier (the
        // snake_case parent_uuid fallback defends against future API
        // normalization, mirroring normalizePolygonCasing). Children the
        // user hid via the eye toggle don't cut holes — hiding a window
        // closes the wall back up rather than leaving an unexplained gap.
        const childrenByWall = new Map();
        [...doors, ...windows, ...openings].forEach(child => {
            const parentId = child.parentIdentifier ?? child.parent_uuid ?? null;
            if (!parentId || hiddenIds.has(child.identifier)) return;
            if (!childrenByWall.has(parentId)) childrenByWall.set(parentId, []);
            childrenByWall.get(parentId).push(child);
        });

        // Walls. Always built — per-object visibility (including walls) is
        // applied by the hiddenIds filter at the end of this memo.
        //
        // Walls render through the polygon-extrusion path whenever an
        // outline is derivable (explicit polygonCorners, else a rectangle
        // synthesized from dimensions — same trick as the measurement
        // labels below), with the visible children's rects cut out (see
        // computeWallHoles). buildWallRenderEntry keeps the render MODE
        // stable across hole changes — see its invariant comment.
        walls.forEach(wall => {
            const children = childrenByWall.get(wall.identifier) ?? [];
            combined.push({
                ...wall,
                renderType: 'wall',
                ...buildWallRenderEntry(wall, children),
                ...getRenderStyle('wall')
            });
        });

        // Doors
        doors.forEach(door => {
            combined.push({
                ...door,
                renderType: 'door',
                ...getRenderStyle('door')
            });
        });

        // Windows
        windows.forEach(win => {
            combined.push({
                ...win,
                renderType: 'window',
                ...getRenderStyle('window')
            });
        });

        // Floors — render as solid slabs sitting on the ground plane. The
        // CapturedObjectMesh polygon-extrusion path already special-cases
        // renderType==='floor' to use FLOOR_THICKNESS_M, so L-shaped
        // polygons render with their true footprint instead of a bounding
        // rectangle. Floors sit below objects so an opaque floor doesn't
        // occlude furniture.
        floors.forEach(floor => {
            combined.push({
                ...floor,
                renderType: 'floor',
                ...getRenderStyle('floor'),
            });
        });

        // Openings — doorless passages. Solid panels in the same brown
        // family as doors; they sit in wall gaps so opaque is consistent
        // with the now-opaque walls.
        openings.forEach(opening => {
            combined.push({
                ...opening,
                renderType: 'opening',
                ...getRenderStyle('opening'),
            });
        });

        // Unknown — items the backend ingest didn't recognize. We don't
        // know their schema, so we defensively backfill missing fields
        // with safe values: identity transform if missing, 0.3m cube
        // dimensions if missing. They render as small amber boxes —
        // visible to the user as "something arrived from iOS we don't
        // understand yet" but quarantined from interpreting fields that
        // might not exist or might have unexpected shapes.
        unknown.forEach(group => {
            if (!group || !Array.isArray(group.items)) return;
            group.items.forEach((item, idx) => {
                if (!item || typeof item !== 'object') return;
                const id = item.identifier ?? item.id ?? `unknown:${group.key}:${idx}`;
                combined.push({
                    ...item,
                    identifier: id,
                    renderType: 'unknown',
                    // Fallback dims: 30cm cube. Forced non-zero so the
                    // mesh isn't a degenerate point.
                    dimensions: Array.isArray(item.dimensions) && item.dimensions.length >= 3
                        ? item.dimensions
                        : [0.3, 0.3, 0.3],
                    // Identity transform if missing — places it at origin.
                    transform: Array.isArray(item.transform) && item.transform.length === 16
                        ? item.transform
                        : [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
                    opacity: 0.7,
                    colorOverride: '#f59e0b',  // amber-500, matches sidebar
                });
            });
        });

        // Ghosts — the editor's not-yet-added previews (a pending wall, or a
        // pending door/window/opening on its wall). Rendered through the same
        // path as their real counterpart (polygon extrusion for walls, box +
        // thickness for openings) but translucent, blue, and flagged
        // non-interactive. Markers that carry their own colorOverride/opacity
        // (anchor-corner previews) keep them; everything else gets the
        // classic pending-blue. Deliberately excluded from onSceneDataLoad
        // above, so they never reach the SceneObjectList.
        ghosts.forEach(ghostSurface => {
            const categoryKey = Object.keys(ghostSurface.category || {})[0];
            const renderType = ['door', 'window', 'opening'].includes(categoryKey) ? categoryKey : 'wall';
            combined.push({
                ...ghostSurface,
                renderType,
                ghost: true,
                opacity: ghostSurface.opacity ?? 0.25,
                colorOverride: ghostSurface.colorOverride || '#3b82f6',  // blue-500 — clearly "pending" next to wall gray
            });
        });

        // Per-object visibility: drop hidden meshes entirely (filtered out,
        // not opacity-0 — a transparent mesh would still raycast and pick up
        // clicks). Keyed by the same `identifier` the SceneObjectList rows use.
        return combined.filter(o => !hiddenIds.has(o.identifier));
    }, [objects, doors, windows, walls, floors, openings, unknown, ghosts, hiddenIds]);

    // Show error state with retry option
    if (error) {
        return (
            <div className={`flex flex-col ${className} bg-[var(--bg-elevated)] rounded-xl overflow-hidden shadow-[var(--shadow-sm)]`}>
                <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center max-w-sm">
                        <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[var(--error-container)] flex items-center justify-center">
                            <svg className="w-6 h-6 text-[var(--error)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-[var(--text-main)] mb-2">Unable to Load Floor Plan</h3>
                        <p className="text-sm text-[var(--text-secondary)] mb-4">{error}</p>
                        <button
                            onClick={loadData}
                            className="px-4 py-2 bg-[var(--primary)] text-[var(--primary-text)] text-sm font-medium rounded-lg hover:opacity-90 transition"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`flex flex-col ${className} bg-[var(--bg-elevated)] rounded-xl overflow-hidden shadow-[var(--shadow-sm)]`}>
            {/* 3D Viewer Container */}
            <div className="relative flex-1 min-h-0">
                {/* Controls */}
                <div className="absolute top-4 left-4 z-10 flex flex-col space-y-2">
                    {/* Camera presets. Folds the old 3D/2D toggle into a
                        single exclusive group: Top (orthographic top-down),
                        3rd (elevated overview), 1st (eye-level inside the
                        room). Selecting a preset sets cameraPreset; viewMode
                        is derived from it. */}
                    <div
                        className="bg-[var(--bg-card)] rounded-lg shadow-[var(--shadow-sm)] p-1 flex space-x-1"
                        role="group"
                        aria-label="Camera view"
                    >
                        {[
                            { value: 'top', label: 'Top' },
                            { value: '3rd', label: '3rd' },
                            { value: '1st', label: '1st' },
                        ].map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                aria-pressed={cameraPreset === option.value}
                                onClick={() => setCameraPreset(option.value)}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${cameraPreset === option.value ? 'bg-[var(--primary)] text-[var(--primary-text)] shadow' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'}`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>

                    {/* Measurements Toggle. Off by default — labels can
                        crowd the view in dense scans, so the user opts in
                        when they need to read edge lengths. */}
                    <button
                        onClick={() => setShowMeasurements(v => !v)}
                        aria-pressed={showMeasurements}
                        className={`bg-[var(--bg-card)] px-3 py-1.5 rounded-lg shadow-[var(--shadow-sm)] text-sm font-medium transition ${showMeasurements ? 'text-[var(--primary)] ring-1 ring-[var(--primary)]' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'}`}
                    >
                        {showMeasurements ? 'Hide Measurements' : 'Show Measurements'}
                    </button>

                    <div
                        className="bg-[var(--bg-card)] rounded-lg shadow-[var(--shadow-sm)] p-1 flex space-x-1"
                        role="group"
                        aria-label="Dimension unit selector"
                    >
                        {getUnitSystemOptions().map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                aria-label={option.ariaLabel}
                                aria-pressed={unitSystem === option.value}
                                onClick={() => handleUnitSystemChange(option.value)}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                                    unitSystem === option.value
                                        ? 'bg-[var(--primary)] text-[var(--primary-text)] shadow'
                                        : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
                                }`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>

                    {/* Sample Toggle (A/B/C Test). Hidden when previewData
                        drives the scene — samples are a no-data dev aid. */}
                    {!roomId && !previewData && (
                        <div className="bg-[var(--bg-card)] rounded-lg shadow-[var(--shadow-sm)] p-1 flex flex-col space-y-1">
                            <div className="flex space-x-1">
                                <button onClick={() => setSampleType('bedroom')} className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition ${sampleType === 'bedroom' ? 'bg-[var(--primary)] text-[var(--primary-text)] shadow' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'}`}>Bedroom</button>
                                <button onClick={() => setSampleType('kitchen')} className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition ${sampleType === 'kitchen' ? 'bg-[var(--primary)] text-[var(--primary-text)] shadow' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'}`}>Kitchen</button>
                            </div>
                            <button onClick={() => setSampleType('multi')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${sampleType === 'multi' ? 'bg-[var(--primary)] text-[var(--primary-text)] shadow' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'}`}>Multi-Room</button>
                        </div>
                    )}
                </div>

                {/* Gridlines toggle. Pinned to the bottom-left, separate from
                    the top-left data controls — this toggles the scene
                    environment (the ground grid), not what scan data is shown.
                    Off by default. Reuses the Measurements-toggle styling. */}
                <div className="absolute bottom-4 left-4 z-10">
                    <button
                        type="button"
                        onClick={() => setShowGrid(v => !v)}
                        aria-pressed={showGrid}
                        className={`bg-[var(--bg-card)] px-3 py-1.5 rounded-lg shadow-[var(--shadow-sm)] text-sm font-medium transition ${showGrid ? 'text-[var(--primary)] ring-1 ring-[var(--primary)]' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'}`}
                    >
                        {showGrid ? 'Hide Grid' : 'Show Grid'}
                    </button>
                </div>

                {/* Canvas. The `style` prop is HTML CSS on the wrapping element,
                    so CSS variables work here. Clean light gradient: near-white
                    (--bg-card #fff) → very-light-gray (--bg-elevated #edeeed) in
                    light mode so white walls pop; the same tokens give a proper
                    dark gradient (#161c1b → #252c2b) in dark mode. Replaces the
                    old --bg-elevated → --bg-dim gradient that read mid-gray. */}
                <Canvas shadows style={{ background: 'linear-gradient(to bottom, var(--bg-card), var(--bg-elevated))', height: '100%' }}>
                    {viewMode === '3d' ? <PerspectiveCamera makeDefault position={[5, 10, 10]} fov={50} /> : <OrthographicCamera makeDefault position={[0, 20, 0]} zoom={20} />}
                    <OrbitControls ref={controlsRef} enableRotate={viewMode === '3d'} maxPolarAngle={Math.PI / 2} />

                    {/* Dollhouse wall-culling — active only in 3D (top-down 2D
                        has no camera-facing walls to hide). */}
                    <WallCuller active={viewMode === '3d'} />
                    {/* Move the camera when the preset changes (3rd / 1st). */}
                    <CameraRig preset={cameraPreset} roomCenter={roomCenter} controlsRef={controlsRef} />

                    {/* Image-based lighting so white (#ffffff) walls actually
                        read white. meshStandardMaterial is physically-based —
                        final color = material × light — and plain ambient +
                        directional lights left white looking gray. A neutral
                        environment map delivers even irradiance from every
                        direction, lifting flat white surfaces to white from
                        all sides (the clean architectural-model look).
                        SceneEnvironment builds it procedurally on the GPU
                        (three's RoomEnvironment) — no async fetch, no Suspense,
                        no third-party CDN for the CSP to block. It lights but
                        does NOT replace the background. Ambient is dialed back
                        and a single soft directional keeps gentle shading for
                        depth without blowing out. */}
                    <ambientLight intensity={0.3} />
                    <directionalLight position={[10, 20, 5]} intensity={0.6} castShadow />
                    <SceneEnvironment />

                    {showGrid && <Grid args={[30, 30]} cellColor="#cbd5e1" sectionColor="#94a3b8" position={[0, -0.01, 0]} />}

                    {/* Single Pass Render Loop. While editor marker ghosts
                        are present, selected surfaces don't mount their own
                        center gizmo — the markers carry the frame gizmo. */}
                    {allRenderObjects.map(obj => (
                        <CapturedObjectMesh
                            key={obj.identifier}
                            data={obj}
                            selectedId={selectedId}
                            onSelect={handleSelect}
                            colorOverride={obj.colorOverride}
                            opacity={obj.opacity}
                            suppressSelectionGizmo={editorMarkersActive}
                        />
                    ))}

                    {/* Per-edge measurement labels. Gated on showMeasurements
                        (the toggle); individually-hidden walls are skipped (no
                        point labeling a wall that isn't drawn). For plain
                        rectangular walls RoomPlan ships an empty
                        polygonCorners array — synthesize the rectangle
                        from dimensions so every wall gets labels, not
                        just the non-rectangular ones. `wall.polygonCorners`
                        is the canonical field thanks to upstream
                        normalization. */}
                    {showMeasurements && walls.map(wall => {
                        if (hiddenIds.has(wall.identifier)) return null;
                        const explicit = wall.polygonCorners;
                        const polygon = (explicit && explicit.length >= 3)
                            ? explicit
                            : inferRectanglePolygon(wall.dimensions);
                        if (!polygon) return null;
                        return (
                            <WallEdgeLabels
                                key={`labels-${wall.identifier}`}
                                polygonCorners={polygon}
                                transform={wall.transform}
                                unitSystem={unitSystem}
                            />
                        );
                    })}

                    {/* Derived wall measurements: gaps between openings,
                        distance from the end openings to the wall corners, and
                        the net (paintable) wall area. Same gating/placement as
                        the edge labels; computed server-side, formatted here. */}
                    {showMeasurements && walls.map(wall => {
                        if (hiddenIds.has(wall.identifier)) return null;
                        const measurement = wallMeasurements[wall.identifier];
                        if (!measurement) return null;
                        return (
                            <OpeningMeasurementLabels
                                key={`spacing-${wall.identifier}`}
                                measurement={measurement}
                                dimensions={wall.dimensions}
                                transform={wall.transform}
                                unitSystem={unitSystem}
                            />
                        );
                    })}

                    {/* Measurement labels for captured furniture objects
                        (W × H × D at the object's center). Answers "see the
                        measurements for other objects". Skips individually
                        hidden objects, same as the wall labels. */}
                    {showMeasurements && objects.map(obj => {
                        if (hiddenIds.has(obj.identifier)) return null;
                        return (
                            <ObjectMeasurementLabels
                                key={`obj-labels-${obj.identifier}`}
                                dimensions={obj.dimensions}
                                transform={obj.transform}
                                unitSystem={unitSystem}
                            />
                        );
                    })}
                </Canvas>
            </div>
        </div>
    );
};

export default FloorPlanViewer;
